import { useMemo, useState } from 'react'
import { Toggle } from '../components/Toggle'
import { TrashIcon } from '../components/icons/TrashIcon'
import { useTranslation } from 'react-i18next'
import { PrincipalSearch } from '../components/PrincipalSearch'
import { Avatar } from '../components/Avatar'
import { Card } from '../components/Card'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InfoTooltip } from '../components/InfoTooltip'
import type { AccessRef, AccessPermission, PrincipalType, PrincipalSearchItem } from '../lib/types'
import { sortAccessPermissions, type AccessSortKey, type AccessSortOrder } from '../lib/accessSort'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/* ─── AccessTab ─── */

export interface AccessTabProps {
  readonly form: AccessRef
  readonly onChange: (patch: Partial<AccessRef>) => void
  readonly agentName: string
}

export function AccessTab({ form, onChange, agentName }: AccessTabProps) {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState<PrincipalType>('User')
  const [dialogPrincipal, setDialogPrincipal] = useState<PrincipalSearchItem | null>(null)
  const [removeCandidate, setRemoveCandidate] = useState<AccessPermission | null>(null)
  const [allowAllConfirmPending, setAllowAllConfirmPending] = useState(false)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<AccessSortKey>('user')
  const [sortOrder, setSortOrder] = useState<AccessSortOrder>('asc')

  const allowAllUsers = form.defaultAccessConfig.grantsInteractionToAllTenantUsers
  const existingIds = form.permissions.map(p => p.principalId)

  const visiblePermissions = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = q
      ? form.permissions.filter(p =>
          (p.principalName + ' ' + (p.principalEmail ?? '')).toLowerCase().includes(q)
        )
      : form.permissions
    return sortAccessPermissions(filtered, sortBy, sortOrder)
  }, [form.permissions, search, sortBy, sortOrder])

  function openDialog() {
    setDialogType('User')
    setDialogPrincipal(null)
    setDialogOpen(true)
  }

  function closeDialog() {
    setDialogOpen(false)
    setDialogPrincipal(null)
  }

  function handleAdd() {
    if (!dialogPrincipal) return
    const newGrant: AccessPermission = {
      principalId: dialogPrincipal.principalId,
      principalType: dialogPrincipal.principalType,
      principalName: dialogPrincipal.principalName,
      principalEmail: dialogPrincipal.principalEmail,
      grantedAt: new Date().toISOString(),
    }
    onChange({ permissions: [...form.permissions, newGrant] })
    closeDialog()
  }

  function handleRemove(principalId: string) {
    onChange({ permissions: form.permissions.filter(p => p.principalId !== principalId) })
  }

  function requestEnableAllowAllUsers() {
    setAllowAllConfirmPending(true)
  }

  function disableAllowAllUsers() {
    onChange({
      defaultAccessConfig: { grantsInteractionToAllTenantUsers: false },
      confirmDefaultAccessToggle: true,
    })
  }

  return (
    <div>
      <Card
        title={t('accessTab.title')}
        subtitle={t('accessTab.subtitle')}
        actions={
          <button
            type="button"
            onClick={openDialog}
            disabled={allowAllUsers}
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '5px 12px', opacity: allowAllUsers ? 0.4 : 1 }}
          >
            {t('accessTab.addUserOrGroup')}
          </button>
        }
      >
        {/* Allow-all-users toggle */}
        <div
          className="toggle-row"
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', marginBottom: 16 }}
        >
          <span
            style={{
              fontSize: 13,
              color: 'var(--text-primary)',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {t('accessTab.allowAllUsers', { agent: agentName })}
            <InfoTooltip width={300}>
              {t('accessTab.allowAllUsersTooltip', { agent: agentName })}
            </InfoTooltip>
          </span>
          <Toggle
            checked={allowAllUsers}
            onChange={(v) => (v ? requestEnableAllowAllUsers() : disableAllowAllUsers())}
            label={t('accessTab.allowAllUsers', { agent: agentName })}
          />
        </div>

        {/* Search + sort — disabled while the allow-all toggle is on (list is non-enforced) */}
        <div className="filter-fields" style={{ marginBottom: 20, opacity: allowAllUsers ? 0.5 : 1 }}>
          <div className="filter-field" style={{ flex: '1 1 220px' }}>
            <label>{t('accessTab.search.label')}</label>
            <input
              type="text"
              className="form-input"
              placeholder={t('accessTab.search.placeholder')}
              value={search}
              disabled={allowAllUsers}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-field" style={{ flex: '0 0 150px' }}>
            <label>{t('accessTab.search.sortLabel')}</label>
            <select
              className="form-select"
              value={sortBy}
              disabled={allowAllUsers}
              onChange={(e) => setSortBy(e.target.value as AccessSortKey)}
            >
              <option value="user">{t('accessTab.search.sortUser')}</option>
              <option value="email">{t('accessTab.search.sortEmail')}</option>
              <option value="added">{t('accessTab.search.sortAdded')}</option>
            </select>
          </div>
          <div className="filter-field" style={{ flex: '0 0 150px' }}>
            <label>{t('accessTab.search.orderLabel')}</label>
            <select
              className="form-select"
              value={sortOrder}
              disabled={allowAllUsers}
              onChange={(e) => setSortOrder(e.target.value as AccessSortOrder)}
            >
              <option value="asc">{t('accessTab.search.orderAsc')}</option>
              <option value="desc">{t('accessTab.search.orderDesc')}</option>
            </select>
          </div>
        </div>

        {/* Grants table */}
        {form.permissions.length === 0 ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            {t('accessTab.noGrants')}
          </div>
        ) : (
          <table
            className="data-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, opacity: allowAllUsers ? 0.5 : 1 }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {t('accessTab.table.userGroup')}
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {t('accessTab.table.granted')}
                </th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {visiblePermissions.map((p, idx) => {
                const isLast = idx === visiblePermissions.length - 1
                const cellBorder = isLast ? 'none' : '1px solid var(--border-light)'

                return (
                  <tr key={p.principalId}>
                    <td style={{ padding: '12px', borderBottom: cellBorder, verticalAlign: 'middle' }}>
                      <div className="access-user">
                        <Avatar className="access-avatar access-avatar-user" size={15} />
                        <div>
                          <div className="access-name">{p.principalName}</div>
                          <div className="access-email">
                            {p.principalType === 'Group' ? 'Group' : (p.principalEmail ?? '')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        borderBottom: cellBorder,
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatDate(p.grantedAt)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: cellBorder, verticalAlign: 'middle' }}>
                      <button
                        type="button"
                        aria-label={`Remove ${p.principalName}`}
                        disabled={allowAllUsers}
                        onClick={() => setRemoveCandidate(p)}
                        className="icon-btn danger"
                        style={{ opacity: allowAllUsers ? 0.4 : 1, pointerEvents: allowAllUsers ? 'none' : 'auto' }}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Remove confirmation dialog */}
      {removeCandidate && (
        <ConfirmDialog
          title={t('accessTab.removeConfirm.title')}
          message={t('accessTab.removeConfirm.message', { name: removeCandidate.principalName })}
          confirmLabel={t('accessTab.removeConfirm.confirm')}
          cancelLabel={t('accessTab.dialog.cancel')}
          danger
          onConfirm={() => {
            handleRemove(removeCandidate.principalId)
            setRemoveCandidate(null)
          }}
          onCancel={() => setRemoveCandidate(null)}
        />
      )}

      {/* Allow-all-users enable confirmation dialog */}
      {allowAllConfirmPending && (
        <ConfirmDialog
          title={t('accessTab.allowAllUsersConfirm.title')}
          message={t('accessTab.allowAllUsersConfirm.message', { agent: agentName })}
          confirmLabel={t('accessTab.allowAllUsersConfirm.confirm')}
          cancelLabel={t('accessTab.dialog.cancel')}
          onConfirm={() => {
            onChange({
              defaultAccessConfig: { grantsInteractionToAllTenantUsers: true },
              confirmDefaultAccessToggle: true,
            })
            setAllowAllConfirmPending(false)
          }}
          onCancel={() => setAllowAllConfirmPending(false)}
        />
      )}

      {/* Add user or group dialog */}
      {dialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('accessTab.dialog.title')}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeDialog() }}
        >
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-lg)',
            width: 440,
            maxWidth: 'calc(100vw - 32px)',
          }}>
            {/* Dialog header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{t('accessTab.dialog.title')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                {t('accessTab.dialog.subtitle')}
              </div>
            </div>

            {/* Dialog body */}
            <div style={{ padding: '20px 24px' }}>
              {/* Type selector */}
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="add-access-type" className="form-label">{t('accessTab.dialog.typeLabel')}</label>
                <select
                  id="add-access-type"
                  value={dialogType}
                  onChange={(e) => {
                    setDialogType(e.target.value as PrincipalType)
                    setDialogPrincipal(null)
                  }}
                  className="form-select"
                >
                  <option value="User">User</option>
                  <option value="Group">Group</option>
                </select>
              </div>

              {/* Typeahead search */}
              <div>
                <label className="form-label">
                  {dialogType === 'User' ? t('accessTab.dialog.searchUsersLabel') : t('accessTab.dialog.searchGroupsLabel')}
                </label>
                <PrincipalSearch
                  type={dialogType}
                  onSelect={(item) => setDialogPrincipal(item)}
                  excludeIds={existingIds}
                />

                {/* Selected principal chip */}
                {dialogPrincipal && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    background: 'var(--accent-subtle)',
                    border: '1px solid var(--accent)',
                    borderRadius: 6,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <Avatar className="access-avatar access-avatar-user" size={13} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{dialogPrincipal.principalName}</div>
                      {dialogPrincipal.principalEmail && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {dialogPrincipal.principalEmail}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={t('accessTab.dialog.clearSelection')}
                      onClick={() => setDialogPrincipal(null)}
                      className="icon-btn"
                      style={{ fontSize: 16, lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Dialog footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={closeDialog}
                className="btn btn-ghost"
              >
                {t('accessTab.dialog.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!dialogPrincipal}
                aria-disabled={!dialogPrincipal}
                className="btn btn-primary"
              >
                {t('accessTab.dialog.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
